import React, { useState, Fragment } from 'react';
import { Button } from '../ui/Button';
import { AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, CoinsIcon } from 'lucide-react';
interface PlanItem {
  id: string;
  clienteId: string;
  clienteNome: string;
  clientePiva: string;
  nomePiano: string;
  importoTotale: number;
  rateScadute: number;
  importoScaduto: number;
  fatture: {
    numero: string;
    data: string;
    importo: number;
  }[];
  avvioPratica: boolean;
}
interface DebtCollectionTableProps {
  searchTerm: string;
}
export function DebtCollectionTable({
  searchTerm
}: DebtCollectionTableProps) {
  const [sortField, setSortField] = useState<keyof PlanItem>('clienteNome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [showCreditConfirmation, setShowCreditConfirmation] = useState<string | null>(null);
  // Dati fittizi per la tabella
  const pianiInadempienti: PlanItem[] = [{
    id: 'plan-1',
    clienteId: '1',
    clienteNome: 'Acme Srl',
    clientePiva: '12345678901',
    nomePiano: 'Piano rientro Q1 2023',
    importoTotale: 12500,
    rateScadute: 2,
    importoScaduto: 5000,
    fatture: [{
      numero: '2023/001',
      data: '2023-01-15',
      importo: 3000
    }, {
      numero: '2023/002',
      data: '2023-02-10',
      importo: 2000
    }],
    avvioPratica: false
  }, {
    id: 'plan-2',
    clienteId: '3',
    clienteNome: 'Industria Meccanica Srl',
    clientePiva: '45678912301',
    nomePiano: 'Piano rientro Marzo-Agosto',
    importoTotale: 18000,
    rateScadute: 3,
    importoScaduto: 9000,
    fatture: [{
      numero: '2023/008',
      data: '2023-03-05',
      importo: 4500
    }, {
      numero: '2023/012',
      data: '2023-04-15',
      importo: 4500
    }],
    avvioPratica: false
  }, {
    id: 'plan-3',
    clienteId: '5',
    clienteNome: 'Alimentari Freschi Spa',
    clientePiva: '56789012341',
    nomePiano: 'Piano rientro Q2 2023',
    importoTotale: 7500,
    rateScadute: 2,
    importoScaduto: 5000,
    fatture: [{
      numero: '2023/031',
      data: '2023-04-05',
      importo: 5000
    }],
    avvioPratica: true
  }];
  // Filtraggio per termine di ricerca
  const filteredPiani = pianiInadempienti.filter(piano => {
    return piano.clienteNome.toLowerCase().includes(searchTerm.toLowerCase()) || piano.clientePiva.includes(searchTerm) || piano.nomePiano.toLowerCase().includes(searchTerm.toLowerCase()) || piano.fatture.some(f => f.numero.toLowerCase().includes(searchTerm.toLowerCase()));
  });
  // Ordinamento dei piani
  const sortedPiani = [...filteredPiani].sort((a, b) => {
    if (sortField === 'clienteNome') {
      return sortDirection === 'asc' ? a.clienteNome.localeCompare(b.clienteNome) : b.clienteNome.localeCompare(a.clienteNome);
    }
    if (sortField === 'nomePiano') {
      return sortDirection === 'asc' ? a.nomePiano.localeCompare(b.nomePiano) : b.nomePiano.localeCompare(a.nomePiano);
    }
    if (sortField === 'importoTotale' || sortField === 'importoScaduto' || sortField === 'rateScadute') {
      return sortDirection === 'asc' ? a[sortField] - b[sortField] : b[sortField] - a[sortField];
    }
    return 0;
  });
  // Funzione per gestire il cambio di ordinamento
  const handleSort = (field: keyof PlanItem) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Gestione dell'espansione delle righe
  const toggleRowExpanded = (id: string) => {
    setExpandedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
  };
  // Formattazione dell'importo in euro
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  // Renderizza l'icona di ordinamento
  const renderSortIcon = (field: keyof PlanItem) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-667" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-668" />;
  };
  // Gestisce il click sul pulsante "Invia al Legale"
  const handleInviaAlLegale = (pianoId: string) => {
    setShowCreditConfirmation(pianoId);
  };
  // Conferma l'invio al legale
  const confirmInviaAlLegale = (pianoId: string) => {
    // Qui andresti a modificare lo stato del piano
    // e a decrementare i crediti disponibili
    setShowCreditConfirmation(null);
  };
  return <div className="overflow-x-auto" data-id="element-669">
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4" data-id="element-670">
        <div className="flex" data-id="element-671">
          <div className="flex-shrink-0" data-id="element-672">
            <AlertCircleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" data-id="element-673" />
          </div>
          <div className="ml-3" data-id="element-674">
            <p className="text-sm text-yellow-700" data-id="element-675">
              Questa sezione mostra i clienti con piani di rateazione attivi ma
              non in regola con i pagamenti. È possibile avviare un recupero
              coatto tramite decreto ingiuntivo con provvisoria esecuzione.
              <span className="ml-1 font-semibold" data-id="element-676">
                L'invio al legale consuma 50 crediti.
              </span>
            </p>
          </div>
        </div>
      </div>
      <table className="min-w-full divide-y divide-gray-200" data-id="element-677">
        <thead className="bg-gray-50" data-id="element-678">
          <tr data-id="element-679">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('clienteNome')} data-id="element-680">
              <div className="flex items-center" data-id="element-681">
                Cliente
                {renderSortIcon('clienteNome')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('nomePiano')} data-id="element-682">
              <div className="flex items-center" data-id="element-683">
                Piano di Rientro
                {renderSortIcon('nomePiano')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('importoTotale')} data-id="element-684">
              <div className="flex items-center" data-id="element-685">
                Importo Totale
                {renderSortIcon('importoTotale')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('rateScadute')} data-id="element-686">
              <div className="flex items-center" data-id="element-687">
                Rate Scadute
                {renderSortIcon('rateScadute')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('importoScaduto')} data-id="element-688">
              <div className="flex items-center" data-id="element-689">
                Importo Scaduto
                {renderSortIcon('importoScaduto')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-690">
              Fatture
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-691">
              Azioni
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-692">
          {sortedPiani.length === 0 ? <tr data-id="element-693">
              <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-694">
                Nessun piano di rientro inadempiente trovato
              </td>
            </tr> : sortedPiani.map(piano => <Fragment key={piano.id} data-id="element-695">
                <tr className="hover:bg-gray-50" data-id="element-696">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-697">
                    <div className="text-sm font-medium text-gray-900" data-id="element-698">
                      {piano.clienteNome}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-699">
                      {piano.clientePiva}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-700">
                    <div className="text-sm text-gray-900" data-id="element-701">
                      {piano.nomePiano}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-702">
                    <div className="text-sm font-medium text-gray-900" data-id="element-703">
                      {formatCurrency(piano.importoTotale)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-704">
                    <div className="text-sm font-medium text-red-600" data-id="element-705">
                      {piano.rateScadute}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-706">
                    <div className="text-sm font-medium text-red-600" data-id="element-707">
                      {formatCurrency(piano.importoScaduto)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-708">
                    <button type="button" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-900" onClick={() => toggleRowExpanded(piano.id)} data-id="element-709">
                      <FileTextIcon className="h-4 w-4 mr-1" data-id="element-710" />
                      Vedi {piano.fatture.length} fatture
                      {expandedRows.includes(piano.id) ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-711" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-712" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-713">
                    {piano.avvioPratica ? <span className="text-green-600" data-id="element-714">Pratica avviata</span> : showCreditConfirmation === piano.id ? <div className="flex items-center justify-end space-x-2" data-id="element-715">
                        <div className="flex items-center text-amber-600" data-id="element-716">
                          <CoinsIcon className="h-4 w-4 mr-1" data-id="element-717" />
                          <span data-id="element-718">50 crediti</span>
                        </div>
                        <button onClick={() => confirmInviaAlLegale(piano.id)} className="bg-[#1A3E5C] text-white px-2 py-1 rounded text-xs" data-id="element-719">
                          Conferma
                        </button>
                        <button onClick={() => setShowCreditConfirmation(null)} className="bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs" data-id="element-720">
                          Annulla
                        </button>
                      </div> : <Button onClick={() => handleInviaAlLegale(piano.id)} data-id="element-721">
                        Invia al Legale
                      </Button>}
                  </td>
                </tr>
                {expandedRows.includes(piano.id) && <tr data-id="element-722">
                    <td colSpan={7} className="px-6 py-4 bg-gray-50" data-id="element-723">
                      <div className="text-sm text-gray-900 font-medium mb-2" data-id="element-724">
                        Elenco Fatture
                      </div>
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-725">
                        <thead className="bg-gray-100" data-id="element-726">
                          <tr data-id="element-727">
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-728">
                              Numero
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-729">
                              Data
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-730">
                              Importo
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-731">
                          {piano.fatture.map((fattura, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-732">
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-733">
                                {fattura.numero}
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-734">
                                {new Date(fattura.data).toLocaleDateString('it-IT')}
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-735">
                                {formatCurrency(fattura.importo)}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </td>
                  </tr>}
              </Fragment>)}
        </tbody>
      </table>
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-736">
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-737">
          <div data-id="element-738">
            <p className="text-sm text-gray-700" data-id="element-739">
              Visualizzazione{' '}
              <span className="font-medium" data-id="element-740">{sortedPiani.length}</span> di{' '}
              <span className="font-medium" data-id="element-741">{pianiInadempienti.length}</span>{' '}
              piani inadempienti
            </p>
          </div>
        </div>
      </div>
    </div>;
}